import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiRouteConstants } from '../../../../../core/constants/api-routes';
import { ApplicationPurposeCodeEnum } from '../../constants/application-purpose-codes';
import { ApplicationCreationResponse } from '../../models/responses/application-creation-response';
import { GridApplicationTemplateRowResponse } from '../../../template/components/application-template-dashboard/models/grid-application-template-row-response';
import { ApplicationTemplateResponse } from '../../../template/shared/models/application-template-response';
import { ApplicationTemplateExistsRequest } from '../../../template/shared/models/application-template-exists-request';
import { ApplicationTemplateRequest } from '../../../template/shared/models/application-template-form-model';
import { ApplicationTemplateCreationFromApplicationRequest } from '../../../template/components/application-template-creation-from-application-form/models/application-template-creation-from-application-request';

@Injectable({
    providedIn: 'root',
})
export class ApplicationTemplateService {
    private readonly applicationTemplateApiRoute =
        ApiRouteConstants.ApplicationTemplate;

    constructor(private readonly http: HttpClient) {}

    /**
     * Creates a new application template in the API.
     */
    createTemplate(
        request: ApplicationTemplateRequest,
    ): Observable<ApplicationCreationResponse> {
        return this.http.post<ApplicationCreationResponse>(
            this.applicationTemplateApiRoute.rootUrl,
            request,
        );
    }

    /**
     * Creates a new application template from an applicatiion in the API.
     */
    createTemplateFromApplication(
        request: ApplicationTemplateCreationFromApplicationRequest,
    ): Observable<ApplicationCreationResponse> {
        return this.http.post<ApplicationCreationResponse>(
            this.applicationTemplateApiRoute.createFromApplication,
            request,
        );
    }

    /**
     * Makes a PUT request to the API to update an existing application template.
     * @param id The ID of the template to update.
     * @param request The updated template data.
     * @returns An observable of the updated template response.
     */
    updateTemplate(
        id: number,
        request: ApplicationTemplateRequest,
    ): Observable<ApplicationTemplateResponse> {
        const route =
            this.applicationTemplateApiRoute.getRootWithTemplateId(id);

        return this.http.put<ApplicationTemplateResponse>(route, request);
    }

    /**
     * Makes a GET request to the API to retrieve a response that contains the information of a single application template.
     * @param id The ID of the template to retrieve.
     * @returns An observable of the template response.
     */
    get(id: number): Observable<ApplicationTemplateResponse> {
        const route =
            this.applicationTemplateApiRoute.getRootWithTemplateId(id);

        return this.http.get<ApplicationTemplateResponse>(route);
    }

    /**
     * Retrieves all current user's application templates from the API.
     *
     * @param applicationPurpose Optional filter to retrieve only templates with a specific purpose.
     */
    getAll(applicationPurpose: ApplicationPurposeCodeEnum | null = null) {
        return this.http.get<GridApplicationTemplateRowResponse[]>(
            this.applicationTemplateApiRoute.getAllTemplatesRoute(
                applicationPurpose,
            ),
        );
    }

    /**
     * Checks if an application template with the given name already exists for the current logged-in user.
     *
     * @param name The name of the application template to check.
     * @returns An observable of a boolean indicating whether the template exists.
     */
    exists(request: ApplicationTemplateExistsRequest): Observable<boolean> {
        return this.http.post<boolean>(
            this.applicationTemplateApiRoute.exists,
            request,
        );
    }

    /**
     * Deletes an application template by its ID.
     * @param id The ID of the template to delete.
     */
    delete(id: number): Observable<void> {
        const route =
            this.applicationTemplateApiRoute.getRootWithTemplateId(id);

        return this.http.delete<void>(route);
    }
}
